/**
 * @file meetingNotesList.ts
 * @brief The Notes page's Meeting notes tab: which meetings it lists, the
 *        line under each, its search, and which personal notes it hides.
 *
 * @details
 * DES 07-10-2026, web port of the phone's NotesListScreen Meeting notes tab
 * (FRONTEND d9397fc, e736654, 386ae73):
 * - Only rows the server marks `tier: 'notes'`: a server without Notes mode
 *   ignores `?tier=notes` and returns every meeting, which listed full
 *   Boardroom meetings as "Meeting notes".
 * - The kind line: "Meeting notes · 32 min · 3 to-dos" (open, not dismissed
 *   to-dos only), or the status while the notes are still being made.
 * - Search covers the title, the overview and the summary.
 * - A personal note whose meeting links ALL point to Notes meetings is the
 *   recorder's Take notes lines: it belongs inside that meeting ("Your
 *   notes"), not also in Personal notes. A note with no links, or linked to
 *   at least one full meeting, stays. The server filters this too once
 *   deployed; this covers older servers.
 *
 * Pure and free of app imports, so `node --test` can load it directly.
 *
 * @references
 * - FRONTEND/src/screens/Notes/NotesListScreen.tsx (MeetingNoteRow, listRows)
 *
 * @author DES
 * @date 07-10-2026
 */

export type MeetingNoteListItem = {
  tier?: string | null;
  status?: string | null;
  title?: string | null;
  meeting_overview?: string | null;
  total_duration_seconds?: number | string | null;
  todos?: Array<{ completed?: boolean | null; dismissedAt?: string | null } | null> | null;
};

const STATUS_LINE: Record<string, string> = {
  uploading: "Transcribing…",
  processing: "Transcribing…",
  synthesis_processing: "Writing your notes…",
  error: "Needs attention",
};

/** Statuses that still change on their own (the list polls while any shows). */
export const MEETING_NOTES_WORKING = new Set(["uploading", "processing", "synthesis_processing"]);

/** Only Meeting Notes recordings (see @details). */
export function onlyNotesMeetings<T extends { tier?: string | null }>(rows: T[]): T[] {
  return rows.filter((m) => m.tier === "notes");
}

/** Notes meetings other people gave this viewer (sharing v2, DES 21-10-2026):
 *  shared with them or invited, never a company admin's view of a member's
 *  meeting. Rows from a server without `sharing` fall back to `is_owner`. */
export function sharedWithMeNotesMeetings<
  T extends { tier?: string | null; is_owner?: boolean | null; sharing?: { viewerRole?: string | null } | null },
>(rows: T[]): T[] {
  return rows.filter((m) => {
    if (m.tier !== "notes" || m.is_owner !== false) return false;
    const role = m.sharing?.viewerRole;
    return !role || role === "shared" || role === "invited" || role === "viewer";
  });
}

export function meetingNoteKindLine(m: MeetingNoteListItem): string {
  const status = m.status ? STATUS_LINE[m.status] : undefined;
  if (status) return status;
  const mins = Math.max(1, Math.round(Number(m.total_duration_seconds ?? 0) / 60));
  const open = (Array.isArray(m.todos) ? m.todos : []).filter(
    (t) => t && !t.completed && !t.dismissedAt,
  ).length;
  return [`Meeting notes · ${mins} min`, open ? `${open} to-do${open === 1 ? "" : "s"}` : null]
    .filter(Boolean)
    .join(" · ");
}

/** Title, overview or summary contains the query (case-insensitive). */
export function filterMeetingNotes<T extends MeetingNoteListItem>(rows: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((m) => {
    const summary = (m as { meeting_summary?: unknown }).meeting_summary;
    return [m.title, m.meeting_overview, typeof summary === "string" ? summary : ""]
      .map((v) => String(v ?? "").toLowerCase())
      .some((v) => v.includes(q));
  });
}

/** Personal notes without the ones that live inside a Notes meeting. */
export function withoutNotesMeetingNotes<
  T extends { meeting_links?: Array<{ meetingId: string }> | null },
>(notes: T[], notesMeetingIds: ReadonlySet<string>): T[] {
  if (notesMeetingIds.size === 0) return notes;
  return notes.filter((n) => {
    const links = n.meeting_links ?? [];
    return links.length === 0 || !links.every((l) => notesMeetingIds.has(l.meetingId));
  });
}
